import '../common/polyfill';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {Route, Switch} from 'inferno-router';

import {BaseApp} from '../common/components/BaseApp';
import {HTTP404Page} from '../common/components/http404';
import lazyLoadComponent from '../common/components/lazyLoadComponent';
import {initConfig} from '../common/config';
import {DialogContainer} from '../common/dialog';
import {getRouter, noop, runIfInBrowser, setupAnalytics} from '../common/util';

import {
    AboutFAQPageRedirect,
    ContactPageRedirect,
    LegalTermsDeveloperUpdateRedirect,
    LegalTermsUpdateRedirect,
    LegalPrivacyRedirect,
} from './redirects';

import {NavBar} from './components/NavBar';
import {DataSaverModeDataSource, GifImgSizesDataSource} from './components/Gif';
import {IsMobileDataSource} from '../common/ui-datasources';
import {TopSearchBar} from './components/TopSearchBar';

// These Components are imported (and "used" by noop) here so their JS and SCSS
// will be included in the same JS and CSS bundle and not lazy loaded. Trying
// to configure wbepack's sideEffects option didn't work.
import {Carousel} from './components/Carousel';
import {SearchTag} from './components/Tag';
import {UniversalGifList} from './components/UniversalGifList';
noop(Carousel, SearchTag, UniversalGifList);

const FeaturedPage = lazyLoadComponent(() => import('./pages/featured'), 'FeaturedPage', -1);
const GifPage = lazyLoadComponent(() => import('./pages/gif'), 'GifPage', -1);
const ExplorePage = lazyLoadComponent(() => import('./pages/explore'), 'ExplorePage', -1);
const ExploreHomePage = lazyLoadComponent(() => import('./pages/explore'), 'ExploreHomePage', -1);
const ReactionsPage = lazyLoadComponent(() => import('./pages/reactions'), 'ReactionsPage', -1);
const SearchPage = lazyLoadComponent(() => import('./pages/search'), 'SearchPage', -1);
const BrandedPartnerPage = lazyLoadComponent(() => import('./pages/profiles'), 'BrandedPartnerPage', -1);
const UserProfilePage = lazyLoadComponent(() => import('./pages/profiles'), 'UserProfilePage', -1);
const PackPage = lazyLoadComponent(() => import('./pages/packs'), 'PackPage', -1);
const CollectionPage = lazyLoadComponent(() => import('./pages/collections'), 'CollectionPage', -1);
const EmotionalGraphPage = lazyLoadComponent(() => import('./pages/about/emotional-graph'), 'EmotionalGraphPage', -1);
const PartnerSpotlightPage = lazyLoadComponent(() => import('./pages/about/partner-spotlight'), 'PartnerSpotlightPage', -1);
const PressPage = lazyLoadComponent(() => import('./pages/about/press'), 'PressPage', -1);
const AboutUsPage = lazyLoadComponent(() => import('./pages/about/about-us'), 'AboutUsPage', -1);
const LegacyAccountRecoverPage = lazyLoadComponent(() => import('./pages/legacy-account-recover'), 'LegacyAccountRecoverPage', -1);
const GifMakerPage = lazyLoadComponent(() => import('./pages/gif-maker'), 'GifMakerPage', -1);
const LegalTermsPage = lazyLoadComponent(() => import('./pages/about/legal-terms'), 'LegalTermsPage', -1);
const IMessagePage = lazyLoadComponent(() => import('./pages/about/imessage'), 'IMessagePage', -1);
const LGLegalDataUsagePage = lazyLoadComponent(() => import('./pages/about/lg'), 'LGLegalDataUsagePage', -1);

const AuthDialog = lazyLoadComponent(() => import('../common/dialogs/AuthDialog'), 'AuthDialog', -1);
const FlaggingDialog = lazyLoadComponent(() => import('./components/FlaggingDialog'), 'FlaggingDialog');
const EditProfileDialog = lazyLoadComponent(() => import('./components/EditProfileDialog'), 'EditProfileDialog', -1);
const ConfirmationDialog = lazyLoadComponent(() => import('../common/dialogs/ConfirmationDialog'), 'ConfirmationDialog', -1);
const StaticImageDialog = lazyLoadComponent(() => import('./components/dialogs/StaticImageDialog'), 'StaticImageDialog', -1);
const CaptionShareDialog = lazyLoadComponent(() => import('./components/CaptionShareDialog'), 'CaptionShareDialog', -1);
const PopupDialog = lazyLoadComponent(() => import('../common/dialogs/PopupDialog'), 'PopupDialog', -1);

import {addLanguagesToContext, getChosenLocale, redirectToLang, setChosenLocale, LanguageSelectionDialog} from './components/dialogs/LanguageSelectionDialog';

import {LanguageBanner, TOSBanner} from '../common/components/Banner';

// We need to import CSS here so that it's not lazily loaded, which would
// result in a flash of unstyled content. If we want to lazily load CSS too,
// we'd need a way to not run JS until the CSS it depends on has loaded.
import '~/assets/dist/iconfont/iconfont.css';
import '~/assets/fonts/fonts.css';
// import animate.css
import 'animate.css/source/_base.css';
import 'animate.css/source/sliding_entrances/slideInLeft.css';
import 'animate.css/source/sliding_entrances/slideInRight.css';
import 'animate.css/source/sliding_entrances/slideInUp.css';
import 'animate.css/source/sliding_exits/slideOutLeft.css';
import 'animate.css/source/sliding_exits/slideOutRight.css';
import 'animate.css/source/sliding_exits/slideOutDown.css';
import 'animate.css/source/fading_entrances/fadeInDown.css';
import 'animate.css/source/fading_exits/fadeOutUp.css';

import '~/assets/scss/bootstrap-replacer.scss';
import '~/assets/scss/ngDialog.min.css';
import '~/assets/scss/ngDialog-theme-default.min.css';
import '~/assets/scss/components/dialog.scss';
import './app.scss';

// SCSS will be included in the same CSS bundle and not lazy loaded.
import '../common/components/ProgressCircle.scss';
import '../common/components/UniversalSearchFormatToggle.scss';
import './components/Carousel.scss';
import './components/Tag.scss';
import './components/UniversalGifList.scss';
import './components/UpsellPill.scss';
import './pages/featured.scss';
import './pages/gif.scss';
import './pages/search.scss';
import './pages/packs.scss';
import './pages/profile.scss';
import './pages/about/about-us.scss';
import './pages/about/legal.scss';
import './pages/about/partner-spotlight.scss';
import './pages/about/press.scss';

export const PATH = '';


class App extends BaseApp {
    fixContext() {
        addLanguagesToContext(this.context);
    }
    beforeChildren() {
        if (process.env.BROWSER) {
            let chosenLocale = getChosenLocale(this.context);
            if (!chosenLocale) {
                const navLang = window.navigator.language.replace('-', '_');
                const exactLang = this.context.languages.find((language) => {
                    return language[0] === navLang;
                });
                if (exactLang) {
                    chosenLocale = exactLang[0];
                } else {
                    const navLangBase = navLang.split('_')[0];
                    const baseLang = this.context.languages.find((language) => {
                        return language[0] === navLangBase;
                    });
                    if (baseLang) {
                        chosenLocale = baseLang[0];
                    } else {
                        chosenLocale = 'en';
                    }
                }
                setChosenLocale(this.context, chosenLocale);
            }
            if (chosenLocale !== this.context.gtService.gt.locale) {
                redirectToLang(this.context, chosenLocale);
            }
        }

        const currentPath = this.context.gtService.delocalizeUrlPath(
            this.context.router.history.location.pathname);
        const showNav = [
            '/imessage',
        ].indexOf(currentPath) === -1;
        const showSearch = [
            '/about-us',
            '/partners',
            '/contentpartners',
            '/imessage',
            '/gif-maker',
        ].indexOf(currentPath) === -1 && !currentPath.includes('/developer');

        return [
            this.allowBanners && <TOSBanner/>,
            <LanguageBanner />,
            showNav && <NavBar/>,
            showSearch && <TopSearchBar/>,
        ];
    }
    afterChildren() {
        return (<DialogContainer
            dialogMap={{
                'auth-dialog': {
                    'class': AuthDialog,
                    'options': {
                        'favoriting': true,
                        'signupAllowed': true,
                        'getLoggedInURL': () => {
                            return this.linkToProfile();
                        },
                        'expiredSession': false,
                        'forceLogInToUpload': false,
                        'loggedinCallback': undefined,
                        'uploadQueue': undefined,
                    },
                },
                'flagging': {'class': FlaggingDialog},
                'edit-profile-dialog': {
                    'class': EditProfileDialog,
                },
                'confirmation-dialog': {
                    'class': ConfirmationDialog,
                    'options': {
                        'isBlockingDialog': true,
                    },
                    'style': {
                        'width': '440px',
                        'max-width': '100%',
                        'padding': '24px',
                    },
                },
                'caption-share-dialog': {
                    'class': CaptionShareDialog,
                },
                'language-selection-dialog': {
                    'class': LanguageSelectionDialog,
                },
                'static-image-dialog': {
                    'class': StaticImageDialog,
                },
                'popup-dialog': {
                    'class': PopupDialog,
                },
                'popup-dialog-blank': {
                    'class': PopupDialog,
                },
            }}
        />);
    }
}


/**
 *
 * @param appGlobals
 */
function routesForLocale(appGlobals) {
    const gtService = appGlobals.gtService;
    const pgettext = gtService.gt.pgettext.bind(gtService.gt);

    let pathPrefix = `/${PATH}`;
    const locale = gtService.gt.locale;
    if (locale && locale !== 'en') {
        pathPrefix = `/${gtService.getLocaleUrlPrefix()}${pathPrefix}`;
    }
    // We need a mapping so we can untranslate paths if needed.
    // This needs to be a function so translating happens on the fly, instead of
    // getting hard coded as one language.
    const pathMap = {
        'gif-maker':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'gif-maker'),
        'search':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'search'),
        'view':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'view'),
        'reactions':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'reactions'),
        'explore':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'explore'),
        'users':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'users'),
        'official':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'official'),
        'packs':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'packs'),
        'collections':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'collections'),
        'emotional-graph':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'emotional-graph'),
        'partners':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'partners'),
        'contentpartners':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'contentpartners'),
        'press':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'press'),
        'about-faq':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'about-faq'),
        'about-us':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'about-us'),
        'imessage':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'imessage'),
        'contact':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'contact'),
        'legacy-account-recover':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legacy-account-recover'),
        'legal-terms':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal-terms'),
        'legal-terms-update':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal-terms-update'),
        'legal-terms-developer-update':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal-terms-developer-update'),
        'legal-privacy':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal-privacy'),
        'legal-privacy-update':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal-privacy-update'),
        'legal/privacy':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal/privacy'),
        'legal/data-usage':
            // Translators: Leave this URL path component untranslated.
            pgettext('url path component', 'legal/data-usage'),
    };
    // This disables translations for url path components.
    for (const path of Object.keys(pathMap)) {
        pathMap[path] = path;
    }
    gtService.setLocalizedPaths(pathMap);

    return <App appGlobals={appGlobals}><Switch>
        <Route exact={true} path={pathPrefix} component={ FeaturedPage }/>
        <Route path={`${pathPrefix}${pathMap['gif-maker']}`} component={ GifMakerPage }/>
        <Route path={`${pathPrefix}${pathMap['search']}/:searchData`} component={ SearchPage }/>
        <Route path={`${pathPrefix}${pathMap['search']}/`} component={ SearchPage }/>
        <Route path={`${pathPrefix}${pathMap['view']}/:id`} component={ GifPage }/>
        <Route path={`${pathPrefix}${pathMap['reactions']}`} component={ ReactionsPage }/>
        <Route path={`${pathPrefix}${pathMap['explore']}/:searchData`} component={ ExplorePage }/>
        <Route path={`${pathPrefix}${pathMap['explore']}`} component={ ExploreHomePage }/>
        <Route path={`${pathPrefix}${pathMap['users']}/:username/:mediatype?`} component={ UserProfilePage }/>
        <Route path={`${pathPrefix}${pathMap['official']}/:username/:mediatype?`} component={ BrandedPartnerPage }/>
        <Route path={`${pathPrefix}${pathMap['packs']}/:id`} component={ PackPage }/>
        <Route path={`${pathPrefix}${pathMap['collections']}/:id`} component={ CollectionPage }/>
        <Route path={`${pathPrefix}${pathMap['emotional-graph']}`} component={ EmotionalGraphPage }/>
        <Route path={`${pathPrefix}${pathMap['partners']}`} component={ PartnerSpotlightPage }/>
        <Route path={`${pathPrefix}${pathMap['contentpartners']}`} component={ PartnerSpotlightPage }/>
        <Route path={`${pathPrefix}${pathMap['press']}`} component={ PressPage }/>
        <Route path={`${pathPrefix}${pathMap['about-faq']}`} component={ AboutFAQPageRedirect }/>
        <Route path={`${pathPrefix}${pathMap['about-us']}`} component={ AboutUsPage }/>
        <Route path={`${pathPrefix}${pathMap['imessage']}`} component={ IMessagePage }/>
        <Route path={`${pathPrefix}${pathMap['contact']}`} component={ ContactPageRedirect }/>
        <Route path={`${pathPrefix}${pathMap['legacy-account-recover']}`} component={ LegacyAccountRecoverPage }/>
        <Route path={`${pathPrefix}${pathMap['legal-terms']}`} component={ LegalTermsPage }/>
        <Route path={`${pathPrefix}${pathMap['legal-terms-update']}`} component={ LegalTermsUpdateRedirect }/>
        <Route path={`${pathPrefix}${pathMap['legal-terms-developer-update']}`} component={ LegalTermsDeveloperUpdateRedirect }/>
        <Route path={`${pathPrefix}${pathMap['legal-privacy']}`} component={ LegalPrivacyRedirect }/>
        <Route path={`${pathPrefix}${pathMap['legal-privacy-update']}`} component={ LegalPrivacyRedirect }/>
        {/**
         * Frank had us set these 2 URLs up, it's linked to from an LG product.
         */}
        <Route path={`${pathPrefix}${pathMap['legal/privacy']}`} component={ LegalPrivacyRedirect }/>
        <Route path={`${pathPrefix}${pathMap['legal/data-usage']}`} component={ LGLegalDataUsagePage }/>
        <Route path="/:shortId*.gif" component={ GifPage }/>
        <Route path="*" component={ HTTP404Page }/>
    </Switch></App>;
}


/**
 *
 * @param history
 * @param config
 * @param request
 * @param response
 * @param {jsx.AppGlobals} context
 */
export function routes(history, config, request, response, context) {
    const appGlobals = initConfig(config, PATH, request, response);
    appGlobals.store.register('ui.dataSaverMode', DataSaverModeDataSource);
    appGlobals.store.register('ui.isMobile', IsMobileDataSource);
    appGlobals.store.register('ui.gifSizes.*', GifImgSizesDataSource);
    appGlobals.store.set('ui.dataSaverMode', true);

    if (context) {
        Object.assign(context, appGlobals);
        for (const [key, val] of Object.entries(appGlobals)) {
            context[key] = val;
        }
    }
    setupAnalytics({context});

    if (process.env.BROWSER) {
        history.listen(function() {
            appGlobals.apiService.newApirefid();
        });

        window.addEventListener('load', function() {
            const {saveData, effectiveType} = (window.navigator['connection'] || {saveData: undefined, effectiveType: undefined});
            window.gaEvent({
                category: 'network-speed',
                action: 'window.navigator.connection.saveData',
                label: saveData,
            });
            window.gaEvent({
                category: 'network-speed',
                action: 'window.navigator.connection.effectiveType',
                label: effectiveType,
            });
            let dataSaverMode = false;
            let dataSaverModeRationale = '';
            if (saveData) {
                dataSaverMode = true;
                dataSaverModeRationale = 'saveData';
            } else if (effectiveType && (effectiveType.endsWith('2g') || effectiveType.endsWith('3g'))) {
                dataSaverMode = true;
                dataSaverModeRationale = 'effectiveType';
            } else if (Date.now() - context.CONSTANTS.START_TS > 3000) {
                dataSaverMode = true;
                dataSaverModeRationale = 'window.load speed';
            }
            window.gaEvent({
                category: 'network-speed',
                action: 'dataSaverModeRationale',
                label: dataSaverModeRationale,
            });
            request.console.log('dataSaverMode', dataSaverMode);
            if (!dataSaverMode) {
                appGlobals.store.set('ui.dataSaverMode', dataSaverMode);
            }
        });
    }

    return getRouter(routesForLocale(appGlobals), history);
}

// Render HTML if in the browser
runIfInBrowser(routes, lazyLoadComponent.promises);
